import React, { useEffect, useState } from 'react';
import T from 'prop-types';
import { Box, Button, Typography } from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { TextField, Dialog } from 'web-components-ts';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { apiGatewaysV2CreateGateway, apiGatewaysV2UpdateGateway } from 'api/gateways_v2';
import { GATEWAY_ID_REGEX } from 'attrs/gateways';
import { COLOR_PRIMARY } from 'attrs/colors';
import { addNotification } from 'redux/ui/notifications/actions';
import { actionStatus, getErrorMessage } from 'redux/utils';
import { generateId } from 'helpers/utils';

const inputSx = {
  width: '100%',
  '& .MuiInputBase-root': {
    width: '100%'
  }
};

const GatewayCrudDialog = ({ gateway, mode, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const isEditMode = mode === 'edit';
  const [gatewayId, setGatewayId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      setGatewayId(gateway?.serial_number || '');
    } else {
      setGatewayId('');
    }
    setErrorMessage('');
    setIsSaving(false);
  }, [gateway?.serial_number, isEditMode, mode]);

  const title = t(isEditMode ? 'gateways.crud.edit.title' : 'gateways.crud.create.title');
  const subtitle = t(isEditMode ? 'gateways.crud.edit.subtitle' : 'gateways.crud.create.subtitle');
  const cancelLabel = t('default_actions.cancel');
  const saveLabel = t('default_actions.save');

  const validateGatewayId = value => {
    if (!value?.trim()) {
      return t('gateways.crud.validation.required');
    }

    if (!GATEWAY_ID_REGEX.test(value.trim())) {
      return t('gateways.crud.validation.invalid_gateway_id');
    }

    return '';
  };

  const handleGatewayIdChange = event => {
    setGatewayId(event?.target?.value || '');

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSave = async () => {
    if (isEditMode && !gateway?.serial_number) {
      setErrorMessage(t('gateways.crud.notifications.generic_error'));
      return;
    }

    const validationMessage = validateGatewayId(gatewayId);
    if (validationMessage) {
      setErrorMessage(validationMessage);
      return;
    }

    setIsSaving(true);

    try {
      const normalizedGatewayId = gatewayId.trim();

      if (isEditMode) {
        await apiGatewaysV2UpdateGateway({
          gatewayId: gateway?.serial_number,
          payload: { new_gateway_id: normalizedGatewayId }
        });
      } else {
        await apiGatewaysV2CreateGateway({
          gateway_id: normalizedGatewayId
        });
      }

      dispatch(
        addNotification({
          key: generateId(),
          type: actionStatus.SUCCESS,
          message: isEditMode
            ? 'gateways.crud.notifications.update_success'
            : 'gateways.crud.notifications.create_success'
        })
      );

      history.push(`/gateways/${normalizedGatewayId}`);
    } catch (err) {
      const error = getErrorMessage(err);
      setErrorMessage(
        t(
          error?.code === 'generic'
            ? 'gateways.crud.notifications.generic_error'
            : `constant_messages.gateway_v2.error.${error.message}`,
          {
            defaultValue: t('gateways.crud.notifications.generic_error')
          }
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!isEditMode && (
        <Typography variant="body2" sx={{ color: '#7A7D85' }}>
          {subtitle}
        </Typography>
      )}
      <TextField
        label={t('gateways.crud.fields.gateway_id')}
        name="gateway-id"
        value={gatewayId}
        onChange={handleGatewayIdChange}
        error={!!errorMessage}
        helperText={errorMessage}
        sx={inputSx}
        inputProps={{
          'data-test-id': isEditMode ? 'edit-gateway-id-input' : 'create-gateway-id-input'
        }}
      />
      {isEditMode && (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('gateways.crud.edit.continue_question')}
        </Typography>
      )}
    </Box>
  );

  const actionBar = (
    <Box
      sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
      data-test-id={isEditMode ? 'edit-gateway-actions' : 'create-gateway-actions'}
    >
      <Button
        variant="text"
        color="error"
        onClick={onClose}
        disabled={isSaving}
        data-test-id={isEditMode ? 'edit-gateway-cancel-button' : 'create-gateway-cancel-button'}
      >
        {cancelLabel}
      </Button>
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={isSaving}
        startIcon={<SaveOutlinedIcon />}
        sx={{ minWidth: 148, backgroundColor: COLOR_PRIMARY }}
        data-test-id={isEditMode ? 'edit-gateway-save-button' : 'create-gateway-save-button'}
      >
        {saveLabel}
      </Button>
    </Box>
  );

  return (
    <Dialog
      open
      handleClose={onClose}
      title={title}
      subTitle=""
      content={content}
      actionBar={actionBar}
      width="100%"
      maxWidth="580px"
      paddingXContent="24px"
      paddingYContent="8px 8px 0"
      data-test-id={isEditMode ? 'edit-gateway-dialog' : 'create-gateway-dialog'}
    />
  );
};

GatewayCrudDialog.propTypes = {
  gateway: T.shape({
    machine_count: T.number,
    serial_number: T.string
  }),
  mode: T.oneOf(['create', 'edit']).isRequired,
  onClose: T.func.isRequired
};

GatewayCrudDialog.defaultProps = {
  gateway: null
};

export default GatewayCrudDialog;
